import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { ClosePanelButton } from './ClosePanelButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Clock, RotateCcw, Edit2, Check, X, GitCompare, FilePlus, ChevronRight } from 'lucide-react';
import { Input } from './ui/input';
import { VersionInspector, type VersionInspectState } from './VersionInspector';
import { VersionHistoryExplanationModal } from './VersionHistoryExplanationModal';
import { ConfirmDialog } from './ConfirmDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import * as Y from 'yjs';
import { buildDocumentContentBody } from '../utils/yjs-content-payload';
import { yjsUpdateFromDocumentApi } from '../utils/document-api-yjs';
import { markDocumentRestoredForReload } from '@narrativebench/editor-core';
import {
  applyVersionInspectorSearchParams,
  filterDocumentVersionsForDisplay,
  findOlderReadyVersionId,
  isVersionInspectable,
  parseVersionInspectorSearchParams,
  type DocumentVersionListItem,
  type VersionInspectorMode,
} from '../utils/document-version-navigation';
import { useTierDefaults } from '../hooks/use-tier-defaults';
import { getHistoryPlanTooltip } from '../utils/tier-plan-features';
import type { UserTier } from '../types/tier-limits';
import { toBcp47Locale } from '../locales/locale';
const VERSION_PAGE_SIZE = 50;

interface VersionHistoryProps {
  documentId: string;
  onVersionRestored?: () => void;
  refreshTrigger?: number;
  isVisible?: boolean;
  onClose?: () => void;
  hideHeading?: boolean;
  canMutate?: boolean;
}

export const VersionHistory: React.FC<VersionHistoryProps> = ({
  documentId,
  onVersionRestored,
  refreshTrigger = 0,
  isVisible = false,
  onClose,
  hideHeading = false,
  canMutate = true,
}) => {
  const { t, locale } = useLocale();
  const { token, user, originalAdminUser } = useAuth();
  const { tierDefaults } = useTierDefaults();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [versions, setVersions] = useState<DocumentVersionListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState<string>('');
  const [savingLabel, setSavingLabel] = useState<string | null>(null);
  const [creatingDocument, setCreatingDocument] = useState<string | null>(null);
  const [includeInvalid, setIncludeInvalid] = useState(false);
  const [namedOnly, setNamedOnly] = useState(false);
  const [manualOnly, setManualOnly] = useState(false);
  const [visibleCount, setVisibleCount] = useState(VERSION_PAGE_SIZE);
  const [historyExplanationOpen, setHistoryExplanationOpen] = useState(false);
  const [restoreConfirmVersionId, setRestoreConfirmVersionId] = useState<string | null>(null);
  const canShowUnavailableVersions = Boolean(user?.admin || originalAdminUser);

  const userTier = (user?.tier || 'free') as UserTier;
  const historyPlanSummary = tierDefaults
    ? getHistoryPlanTooltip(tierDefaults[userTier], t, locale)
    : undefined;

  useEffect(() => {
    if (!canShowUnavailableVersions && includeInvalid) {
      setIncludeInvalid(false);
    }
  }, [canShowUnavailableVersions, includeInvalid]);

  const fetchVersions = React.useCallback(async (showLoading = true): Promise<void> => {
    if (!documentId || !token) {
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      }
      const showInvalid = includeInvalid && canShowUnavailableVersions;
      const query = showInvalid ? '?includeInvalid=true' : '';
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/versions${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const versionsList = Array.isArray(data) ? data : [];
        setVersions(
          showInvalid
            ? versionsList
            : versionsList.filter((version: DocumentVersionListItem) => isVersionInspectable(version)),
        );
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [documentId, token, includeInvalid, canShowUnavailableVersions]);

  useEffect(() => {
    setVisibleCount(VERSION_PAGE_SIZE);
  }, [documentId, includeInvalid, namedOnly, manualOnly]);

  const filteredVersions = useMemo(
    () => filterDocumentVersionsForDisplay(versions, { namedOnly, manualOnly }),
    [versions, namedOnly, manualOnly],
  );

  const emptyFilterMessage = manualOnly
    ? t('versionHistory.noManualVersions')
    : t('versionHistory.noNamedVersions');

  const mostRecentVersionId = versions[0]?.id ?? null;

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions, refreshTrigger]);

  useEffect(() => {
    if (!isVisible || !documentId || !token) {
      return;
    }

    const pollInterval = setInterval(() => {
      fetchVersions(false);
    }, 5000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [isVisible, documentId, token, fetchVersions]);

  const writeInspectorSearchParams = useCallback((state: VersionInspectState | null): void => {
    setSearchParams((previous) => {
      const next = applyVersionInspectorSearchParams(
        previous,
        state
          ? {
              versionId: state.versionId,
              compareVersionId: state.compareVersionId,
              mode: state.mode,
            }
          : null,
      );
      return next.toString() === previous.toString() ? previous : next;
    }, { replace: true });
  }, [setSearchParams]);

  const urlInspectState = useMemo(
    () => parseVersionInspectorSearchParams(searchParams),
    [searchParams],
  );

  const inspectorVersionId = useMemo(() => {
    if (!urlInspectState.versionId || versions.length === 0) {
      return null;
    }
    if (!versions.some((version) => (
      version.id === urlInspectState.versionId && isVersionInspectable(version)
    ))) {
      return null;
    }
    return urlInspectState.versionId;
  }, [urlInspectState.versionId, versions]);

  const inspectorMode: VersionInspectorMode = urlInspectState.mode ?? 'changes';
  const inspectorCompareVersionId = inspectorMode === 'changes'
    ? urlInspectState.compareVersionId
    : null;

  useEffect(() => {
    if (!urlInspectState.versionId || versions.length === 0) {
      return;
    }
    if (!versions.some((version) => (
      version.id === urlInspectState.versionId && isVersionInspectable(version)
    ))) {
      writeInspectorSearchParams(null);
    }
  }, [urlInspectState.versionId, versions, writeInspectorSearchParams]);

  const openInspector = (versionId: string, mode: VersionInspectorMode): void => {
    writeInspectorSearchParams({
      versionId,
      compareVersionId: mode === 'changes'
        ? findOlderReadyVersionId(filteredVersions, versionId)
        : null,
      mode,
    });
  };

  const closeInspector = (): void => {
    writeInspectorSearchParams(null);
  };

  const handleInspectStateChange = useCallback((state: VersionInspectState): void => {
    writeInspectorSearchParams(state);
  }, [writeInspectorSearchParams]);

  const handleRestore = async (versionId: string): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      setRestoring(versionId);
      const response = await fetch(
        `${API_BASE}/api/documents/${documentId}/versions/${versionId}/restore`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        writeInspectorSearchParams(null);
        markDocumentRestoredForReload(documentId);
        await fetchVersions();
        if (onVersionRestored) {
          onVersionRestored();
        }
      } else {
        const error = await response.json();
        alert(error.error || t('versionHistory.restoreError'));
      }
    } catch (error) {
      console.error('Error restoring version:', error);
      alert(t('versionHistory.restoreError'));
    } finally {
      setRestoring(null);
    }
  };

  const requestRestore = (versionId: string): void => {
    setRestoreConfirmVersionId(versionId);
  };

  const handleStartEdit = (version: DocumentVersionListItem): void => {
    setEditingVersionId(version.id);
    setEditingLabel(version.label || '');
  };

  const handleCancelEdit = (): void => {
    setEditingVersionId(null);
    setEditingLabel('');
  };

  const handleSaveLabel = async (versionId: string): Promise<void> => {
    if (!token) {
      return;
    }

    const trimmedLabel = editingLabel.trim();
    const currentVersion = versions.find((version) => version.id === versionId);
    if (currentVersion?.label && !trimmedLabel) {
      if (!confirm(t('versionHistory.confirmRemoveLabel'))) {
        return;
      }
    }

    const isFreePlan = !user?.tier || user.tier === 'free';
    if (
      isFreePlan
      && trimmedLabel
      && versions.some((version) => (
        version.id !== versionId
        && Boolean(version.label && version.label.trim().length > 0)
      ))
    ) {
      if (!confirm(t('versionHistory.confirmReplaceLabel'))) {
        return;
      }
    }

    try {
      setSavingLabel(versionId);
      const response = await fetch(
        `${API_BASE}/api/documents/${documentId}/versions/${versionId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            label: trimmedLabel || null,
          }),
        }
      );

      if (response.ok) {
        await fetchVersions();
        setEditingVersionId(null);
        setEditingLabel('');
      } else {
        const error = await response.json();
        alert(error.error || t('versionHistory.updateError'));
      }
    } catch (error) {
      console.error('Error updating version label:', error);
      alert(t('versionHistory.updateError'));
    } finally {
      setSavingLabel(null);
    }
  };

  const handleCreateDocument = async (versionId: string): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      setCreatingDocument(versionId);

      const [versionResponse, documentResponse] = await Promise.all([
        fetch(
          `${API_BASE}/api/documents/${documentId}/versions/${versionId}?includeState=true`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        fetch(
          `${API_BASE}/api/documents/${documentId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);

      if (!versionResponse.ok) {
        const errorData = await versionResponse.json();
        if (versionResponse.status === 410) {
          throw new Error(t('versionHistory.unavailable'));
        }
        throw new Error(errorData.error || 'Failed to fetch version');
      }

      if (!documentResponse.ok) {
        const errorData = await documentResponse.json();
        throw new Error(errorData.error || 'Failed to fetch document');
      }

      const version = await versionResponse.json();
      const document = await documentResponse.json();

      if (!document.projectId) {
        throw new Error('Document does not belong to a project');
      }

      let restoredState: Uint8Array;
      if (version.format === 'yjs_update' && Array.isArray(version.state) && version.state.length > 0) {
        restoredState = new Uint8Array(version.state);
      } else if (Array.isArray(version.state) && version.state.length > 0) {
        const originYdoc = new Y.Doc({ gc: false });
        const originUpdate = yjsUpdateFromDocumentApi(document);
        if (originUpdate) {
          Y.applyUpdate(originYdoc, originUpdate);
        }
        const snapshot = Y.decodeSnapshot(new Uint8Array(version.state));
        const restoredDoc = Y.createDocFromSnapshot(originYdoc, snapshot);
        restoredState = Y.encodeStateAsUpdate(restoredDoc);
        restoredDoc.destroy();
        originYdoc.destroy();
      } else {
        throw new Error('Version state is empty');
      }

      const versionLabel = version.label || t('versionHistory.automatic');
      const title = `${document.title} - ${versionLabel}`;

      const createResponse = await fetch(`${API_BASE}/api/projects/${document.projectId}/documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          ...buildDocumentContentBody(restoredState),
        }),
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || 'Failed to create document');
      }

      const newDocument = await createResponse.json();
      navigate(`/projects/${document.projectId}/documents/${newDocument.id}`);
    } catch (error) {
      console.error('Error creating document from version:', error);
      alert(error instanceof Error ? error.message : t('versionHistory.createDocumentError'));
    } finally {
      setCreatingDocument(null);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(toBcp47Locale(locale), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const visibleVersions = filteredVersions.slice(0, visibleCount);

  return (
    <div>
      {hideHeading ? null : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: 'var(--foreground)' }}>{t('versionHistory.title')}</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {onClose && (
              <ClosePanelButton onClick={onClose} ariaLabel={t('close')} />
            )}
          </div>
        </div>
      )}
      {historyPlanSummary && (
        <div className="mb-6 space-y-2">
          <p className="text-sm text-muted-foreground m-0">
            {historyPlanSummary}
          </p>
          <button
            type="button"
            onClick={() => setHistoryExplanationOpen(true)}
            className="inline-flex items-center gap-1 text-left text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/80"
          >
            {t('how_version_history_works')}
            <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
        <div className="flex items-center gap-2">
          <Switch
            id="show-named-versions-only"
            checked={namedOnly}
            onCheckedChange={setNamedOnly}
          />
          <Label htmlFor="show-named-versions-only" className="text-sm font-normal text-muted-foreground cursor-pointer">
            {t('versionHistory.showNamedOnly')}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="show-manual-versions-only"
            checked={manualOnly}
            onCheckedChange={setManualOnly}
          />
          <Label htmlFor="show-manual-versions-only" className="text-sm font-normal text-muted-foreground cursor-pointer">
            {t('versionHistory.showManualOnly')}
          </Label>
        </div>
        {canShowUnavailableVersions && (
          <div className="flex items-center gap-2">
            <Switch
              id="show-unavailable-versions"
              checked={includeInvalid}
              onCheckedChange={setIncludeInvalid}
            />
            <Label htmlFor="show-unavailable-versions" className="text-sm font-normal text-muted-foreground cursor-pointer">
              {t('versionHistory.showUnavailable')}
            </Label>
          </div>
        )}
      </div>
      <div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted-foreground)' }}>{t('versionHistory.loading')}</div>
        ) : versions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted-foreground)' }}>{t('versionHistory.noVersions')}</div>
        ) : filteredVersions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted-foreground)' }}>{emptyFilterMessage}</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('versionHistory.date')}</TableHead>
                  <TableHead>{t('versionHistory.createdBy')}</TableHead>
                  <TableHead>{t('versionHistory.label')}</TableHead>
                  <TableHead>{t('versionHistory.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleVersions.map((version) => {
                  const isMostRecent = version.id === mostRecentVersionId;
                  const inspectable = isVersionInspectable(version);
                  const restoreAllowed = inspectable && version.canRestore !== false && version.status !== 'pending_migration';
                  return (
                    <TableRow
                      key={version.id}
                      className={
                        inspectable
                          ? 'cursor-pointer hover:bg-muted/50'
                          : 'opacity-50 cursor-not-allowed'
                      }
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('input')) {
                          return;
                        }
                        if (!inspectable) {
                          return;
                        }
                        openInspector(version.id, 'fullText');
                      }}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{formatDate(version.createdAt)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {version.userEmail || t('versionHistory.system')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {editingVersionId === version.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editingLabel}
                                onChange={(e) => setEditingLabel(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveLabel(version.id);
                                  } else if (e.key === 'Escape') {
                                    handleCancelEdit();
                                  }
                                }}
                                className="h-8 text-sm"
                                autoFocus
                                disabled={savingLabel === version.id}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveLabel(version.id);
                                }}
                                disabled={savingLabel === version.id}
                                className="h-8 w-8 p-0"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelEdit();
                                }}
                                disabled={savingLabel === version.id}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 group">
                              {version.label ? (
                                <span>{version.label}</span>
                              ) : (
                                <Badge variant="secondary">{t('versionHistory.automatic')}</Badge>
                              )}
                              {canMutate && inspectable && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartEdit(version);
                                  }}
                                  disabled={restoring === version.id || savingLabel === version.id}
                                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          )}
                          {version.description && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {version.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {inspectable ? (
                          <div className="flex items-center gap-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openInspector(version.id, 'changes');
                                    }}
                                    disabled={restoring === version.id || savingLabel === version.id || creatingDocument === version.id}
                                  >
                                    <GitCompare className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t('versionHistory.changesInThisVersionTooltip')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            {canMutate && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCreateDocument(version.id);
                                      }}
                                      disabled={restoring === version.id || savingLabel === version.id || creatingDocument === version.id}
                                    >
                                      <FilePlus className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('versionHistory.createDocumentTooltip')}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {canMutate && !isMostRecent && restoreAllowed && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        requestRestore(version.id);
                                      }}
                                      disabled={restoring === version.id}
                                    >
                                      <RotateCcw className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('versionHistory.backToThisVersionTooltip')}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-xs text-muted-foreground">
                                  {t('versionHistory.unavailable')}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('versionHistory.unavailableTooltip')}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {visibleCount < filteredVersions.length && (
              <div className="flex justify-center py-4">
                <Button
                  variant="outline"
                  onClick={() => setVisibleCount((count) => count + VERSION_PAGE_SIZE)}
                >
                  {t('versionHistory.loadMore')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <VersionInspector
        isOpen={inspectorVersionId !== null}
        onClose={closeInspector}
        documentId={documentId}
        versions={filteredVersions}
        versionId={inspectorVersionId}
        onVersionIdChange={(nextVersionId) => {
          writeInspectorSearchParams({
            versionId: nextVersionId,
            compareVersionId: inspectorMode === 'changes'
              ? findOlderReadyVersionId(filteredVersions, nextVersionId)
              : null,
            mode: inspectorMode,
          });
        }}
        initialMode={inspectorMode}
        initialCompareVersionId={inspectorCompareVersionId}
        onInspectStateChange={handleInspectStateChange}
        onRestore={canMutate ? requestRestore : undefined}
        onCreateDocument={canMutate ? handleCreateDocument : undefined}
        restoring={restoring !== null}
        creatingDocument={creatingDocument !== null}
      />
      {canMutate && (
        <ConfirmDialog
          open={restoreConfirmVersionId !== null}
          onOpenChange={(open) => {
            if (!open) {
              setRestoreConfirmVersionId(null);
            }
          }}
          destructive={false}
          title={t('versionHistory.backToThisVersion')}
          description={t('versionHistory.confirmRestore')}
          confirmLabel={t('versionHistory.backToThisVersion')}
          onConfirm={() => {
            if (restoreConfirmVersionId) {
              void handleRestore(restoreConfirmVersionId);
            }
          }}
        />
      )}
      <VersionHistoryExplanationModal
        open={historyExplanationOpen}
        onOpenChange={setHistoryExplanationOpen}
      />
    </div>
  );
};
